<script setup lang="ts">
interface Props {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  type?: 'button' | 'submit' | 'reset'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  type: 'button',
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="[
      'base-button',
      `base-button--${variant}`,
      `base-button--${size}`,
      { 'base-button--loading': loading }
    ]"
    @click="handleClick"
  >
    <span v-if="loading" class="base-button__loader">
      <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </span>
    <span :class="{ 'opacity-0': loading }">
      <slot />
    </span>
  </button>
</template>

<style scoped>
.base-button {
  @apply relative inline-flex items-center justify-center gap-2 font-semibold 
         rounded-lg transition-all duration-200 
         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark;
  font-family: var(--font-display);
}

.base-button:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.base-button:not(:disabled):active {
  @apply scale-[0.98];
}

/* Variants */
.base-button--primary {
  @apply bg-primary text-dark hover:bg-primary-400 focus:ring-primary;
  box-shadow: 0 0 0 0 rgba(18, 192, 221, 0);
}

.base-button--primary:not(:disabled):hover {
  box-shadow: 0 0 20px rgba(18, 192, 221, 0.4);
}

.base-button--secondary {
  @apply bg-secondary text-white hover:bg-secondary-400 focus:ring-secondary;
}

.base-button--tertiary {
  @apply bg-tertiary text-white hover:bg-tertiary-400 focus:ring-tertiary;
}

.base-button--danger {
  @apply bg-red-600 text-white hover:bg-red-500 focus:ring-red-500;
}

.base-button--ghost {
  @apply bg-transparent text-neutral-300 hover:bg-dark-400 hover:text-neutral-100 focus:ring-primary;
}

/* Sizes */
.base-button--sm {
  @apply px-3 py-1.5;
  font-size: var(--text-sm);
}

.base-button--md {
  @apply px-5 py-2.5;
  font-size: var(--text-sm);
}

.base-button--lg {
  @apply px-6 py-3;
  font-size: var(--text-base);
}

/* Loading state */
.base-button--loading {
  @apply cursor-wait;
}

.base-button__loader {
  @apply absolute inset-0 flex items-center justify-center;
}
</style>
